"use client";

import { useSessionWorkspace } from "../providers/org_provider";
import { useUser } from "../providers/user_provider";
import {
  FeatureFlagPayload,
  isFeatureEnabledFromPayload,
} from "@/utils/feature_flag/shared";
import { OrganizationId, SurveyId, UserInfo } from "@/utils/types";
import { useFeatureFlagPayload } from "posthog-js/react";

/**
 * We have the following priority of showing the children:
 * 1. always show if forceShow is true
 * 2. never show if forceHide is true
 * 3. if override is set, use that
 * 4. else use feature flag
 */
export function FeatureFlagGatedComp({
  featureFlag,
  override,
  forceshow,
  forceHide,
  children,
  fallback = null,
  payloadProps,
}: {
  featureFlag: string;
  override?: boolean;
  forceshow?: boolean;
  forceHide?: boolean;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  payloadProps?: {
    survey_id?: SurveyId;
    user?: UserInfo | null;
    orgId?: OrganizationId | null;
    enabledForSuperAdmin?: boolean;
  };
}) {
  const renderChildren = isFeatureFlagEnabledClient(
    featureFlag,
    payloadProps ?? {}
  );
  if (forceshow) {
    return <>{children}</>;
  }
  if (forceHide) {
    return fallback;
  }
  if (override !== undefined) {
    return <>{override ? children : fallback}</>;
  }
  return <>{renderChildren ? children : fallback}</>;
}

export const isFeatureFlagEnabledClient = (
  featureFlag: string,
  props: {
    survey_id?: SurveyId;
    user?: UserInfo | null;
    orgId?: OrganizationId | null;
    enabledForSuperAdmin?: boolean;
    onlyActiveOrg?: boolean;
  }
): boolean => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const payload = useFeatureFlagPayload(featureFlag) as
    | FeatureFlagPayload
    | undefined;

  if (props.enabledForSuperAdmin && props.user?.isSuperAdmin) {
    return true;
  }
  if (!payload) {
    // to enable globally you need to set the "enabled" property in the payload
    return false;
  }
  return isFeatureEnabledFromPayload({ payload, ...props });
};

export const useIsFeatureFlagEnabledForUserViaContext = (
  featureFlag: string,
  options?: {
    onlyActiveOrg?: boolean;
    overrideOrgId?: OrganizationId | null;
    surveyId?: SurveyId | null;
  }
) => {
  const { user } = useUser();
  const { org: workspace } = useSessionWorkspace();
  const payload = useFeatureFlagPayload(featureFlag) as
    | FeatureFlagPayload
    | undefined;

  if (!payload) {
    // to enable globally you need to set the "enabled" property in the payload
    return false;
  }

  return isFeatureEnabledFromPayload({
    payload,
    user,
    orgId: options?.overrideOrgId ?? workspace?.id,
    onlyActiveOrg: options?.onlyActiveOrg,
    survey_id: options?.surveyId,
  });
};
